/*
 * Consumer session — localStorage-backed so the session survives new tabs and
 * browser restarts within the token lifetime (tab-scoped sessionStorage made a
 * really-signed-in user look logged out in a fresh tab — one ingredient of the
 * 2026-08-04 mock-leak incident). Expiry is enforced on load.
 */
import type { TokenSet, IdClaims } from './oidc'
import { decodeJwtPayload } from './oidc'

const SESSION_KEY = 'mytrumin.session.v1'
const FLIGHT_KEY = 'mytrumin.authflight.v1'

/**
 * Cognito self-service scope. GetUser / UpdateUserAttributes (the Profile
 * account card + the profile wizard, via src/auth/cognitoProfile.ts) require
 * the ACCESS token to carry this scope, else they 400 'Access Token does not
 * have required scopes'.
 *
 * The email-first SRP login (CMT-046) mints access tokens that ALWAYS carry it
 * — this is intrinsic to InitiateAuth/RespondToAuthChallenge and does NOT
 * depend on the app-client's AllowedOAuthScopes. The now-retired hosted-UI/PKCE
 * path (oidc.ts + AuthCallback.tsx) minted access tokens scoped ONLY by
 * AllowedOAuthScopes, which could lack this scope. Because both paths persist
 * under the SAME SESSION_KEY, a hosted-UI session minted BEFORE the CMT-046
 * cutover can survive (unexpired) into the SRP era and, being reused verbatim,
 * makes Profile's GetUser 400. Treat such a session as stale so the auth gate
 * re-authenticates via SRP and mints a properly-scoped token
 * (CMT-2026-08-09-034).
 */
const REQUIRED_ACCESS_SCOPE = 'aws.cognito.signin.user.admin'

/**
 * True when the access token can service Cognito GetUser / UpdateUserAttributes.
 * A Cognito access token always carries a space-delimited `scope` claim; if it
 * is present and does NOT include the self-service scope, the token is a stale
 * hosted-UI token that cannot self-service. Non-JWT / claim-absent tokens are
 * treated as usable — never over-invalidate a session on a token we cannot read
 * (session lifetime is otherwise governed by expiry).
 */
export function accessTokenCanSelfService(accessToken: string): boolean {
  let scope: unknown
  try {
    scope = (decodeJwtPayload(accessToken) as { scope?: unknown }).scope
  } catch {
    return true
  }
  if (typeof scope !== 'string') return true
  return scope.split(' ').includes(REQUIRED_ACCESS_SCOPE)
}

export interface Session {
  idToken: string
  accessToken: string
  refreshToken?: string
  expiresAt: number // epoch ms
  claims: IdClaims
}

export function storeSession(tokens: TokenSet, now = Date.now()): Session {
  const session: Session = {
    idToken: tokens.idToken,
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: now + tokens.expiresIn * 1000,
    claims: decodeJwtPayload(tokens.idToken),
  }
  localStorage.setItem(SESSION_KEY, JSON.stringify(session))
  return session
}

export function loadSession(now = Date.now()): Session | null {
  const raw = localStorage.getItem(SESSION_KEY)
  if (!raw) return null
  try {
    const session = JSON.parse(raw) as Session
    if (session.expiresAt <= now) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    // Stale hosted-UI/PKCE token surviving the CMT-046 SRP cutover — its access
    // token lacks the self-service scope, so Cognito GetUser/UpdateUserAttributes
    // 400. Drop it so the auth gate re-authenticates via SRP (mints a scoped
    // token). Fresh SRP sessions always carry the scope and pass through.
    if (!accessTokenCanSelfService(session.accessToken)) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    return session
  } catch {
    localStorage.removeItem(SESSION_KEY)
    return null
  }
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY)
}

/**
 * @deprecated In-flight PKCE state for the hosted-UI redirect. The email-first
 * login authenticates in-app (no redirect), so no flight is stored. Retained so
 * /auth/callback can drain any redirect already in flight for one deploy cycle.
 */
export interface AuthFlight {
  verifier: string
  state: string
  redirectUri: string
}

export function storeFlight(flight: AuthFlight): void {
  localStorage.setItem(FLIGHT_KEY, JSON.stringify(flight))
}

export function takeFlight(): AuthFlight | null {
  const raw = localStorage.getItem(FLIGHT_KEY)
  localStorage.removeItem(FLIGHT_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as AuthFlight
  } catch {
    return null
  }
}
